import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, MapPin, Music, Send, Youtube, Instagram, Facebook, Twitter, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success(t('Message sent successfully!', '¡Mensaje enviado con éxito!'));
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('Failed to send message. Please try again.', 'Error al enviar el mensaje. Por favor intente de nuevo.'));
    } finally {
      setLoading(false);
    }
  };

  const socialLinks = [
    { icon: Youtube, href: 'https://www.youtube.com/sfrodelly', label: 'YouTube', color: 'hover:bg-red-600' },
    { icon: Instagram, href: 'https://www.instagram.com/sarah.capeles/', label: 'Instagram', color: 'hover:bg-pink-600' },
    { icon: Facebook, href: 'https://www.facebook.com/sarah.capeles', label: 'Facebook', color: 'hover:bg-blue-600' },
    { icon: Twitter, href: 'https://www.twitter.com/sarahcapeles', label: 'Twitter/X', color: 'hover:bg-sky-500' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-charcoal">
        <div className="container mx-auto px-4 md:px-8">
          <p className="font-lato text-gold-400 text-sm uppercase tracking-[0.3em] mb-2">
            {t('Get in Touch', 'Contacto')}
          </p>
          <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-4">
            {t('Contact', 'Contacto')}
          </h1>
          <p className="font-lato text-xl text-gray-400 max-w-2xl">
            {t(
              'Book Sarah for your church service, evangelistic campaign, concert, or special event. We\'d love to hear from you.',
              'Reserve a Sarah para su servicio de iglesia, campaña evangelística, concierto o evento especial. Nos encantaría saber de usted.'
            )}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-playfair text-3xl font-bold text-charcoal mb-8">
                {t('Contact Information', 'Información de Contacto')}
              </h2>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Music className="w-6 h-6 text-gold-600" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold text-charcoal">
                      {t('Artist', 'Artista')}
                    </h3>
                    <p className="font-lato text-gray-600">Sarah Capeles</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-gold-600" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold text-charcoal">
                      {t('Location', 'Ubicación')}
                    </h3>
                    <p className="font-lato text-gray-600">New Jersey, USA</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-gold-600" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-lg font-semibold text-charcoal">
                      {t('Contact Method', 'Método de Contacto')}
                    </h3>
                    <p className="font-lato text-gray-600">
                      {t('Use the form or social media', 'Use el formulario o redes sociales')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-playfair text-lg font-semibold text-charcoal mb-4">
                  {t('Connect on Social Media', 'Conecta en Redes Sociales')}
                </h3>
                <div className="flex gap-4">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center transition-colors ${social.color}`}
                      data-testid={`social-${social.label.toLowerCase()}`}
                    >
                      <social.icon className="w-5 h-5 text-gray-700 hover:text-white" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Scripture */}
              <div className="mt-12 p-6 bg-cream rounded-xl">
                <blockquote className="font-playfair text-lg text-charcoal italic">
                  {t(
                    '"Let everything that has breath praise the Lord. Praise the Lord!"',
                    '"Todo lo que respira alabe al Señor. ¡Aleluya!"'
                  )}
                </blockquote>
                <p className="font-lato text-gold-600 text-sm uppercase tracking-wider mt-2">
                  — {t('Psalm 150:6', 'Salmo 150:6')}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-xl border border-gray-100">
              {submitted ? (
                <div className="text-center py-12" data-testid="success-message">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="font-playfair text-2xl font-bold text-charcoal mb-2">
                    {t('Message Sent!', '¡Mensaje Enviado!')}
                  </h3>
                  <p className="font-lato text-gray-600 mb-6">
                    {t(
                      'Thank you for reaching out. We will get back to you soon.',
                      'Gracias por comunicarse. Nos pondremos en contacto pronto.'
                    )}
                  </p>
                  <Button 
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="border-gold-500 text-gold-600"
                  >
                    {t('Send Another Message', 'Enviar Otro Mensaje')}
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="font-playfair text-2xl font-bold text-charcoal mb-2">
                    {t('Send a Message', 'Enviar un Mensaje')}
                  </h2>
                  <p className="font-lato text-gray-600 mb-6">
                    {t(
                      'Fill out the form below and we\'ll get back to you as soon as possible.',
                      'Complete el formulario a continuación y nos comunicaremos lo antes posible.'
                    )}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="font-lato text-charcoal">
                          {t('Name', 'Nombre')} *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-1 border-gray-300 focus:border-gold-500 focus:ring-gold-500"
                          placeholder={t('Your name', 'Su nombre')}
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="font-lato text-charcoal">
                          {t('Email', 'Correo Electrónico')} *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-1 border-gray-300 focus:border-gold-500 focus:ring-gold-500"
                          placeholder={t('your@email.com', 'su@correo.com')}
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject" className="font-lato text-charcoal">
                        {t('Subject', 'Asunto')} *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="mt-1 border-gray-300 focus:border-gold-500 focus:ring-gold-500"
                        placeholder={t('e.g. Booking Inquiry, Event Request', 'ej. Consulta de Reserva, Solicitud de Evento')}
                        data-testid="input-subject"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="font-lato text-charcoal">
                        {t('Message', 'Mensaje')} *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="mt-1 border-gray-300 focus:border-gold-500 focus:ring-gold-500"
                        placeholder={t(
                          'Tell us about your event, date, location, and any other details...',
                          'Cuéntenos sobre su evento, fecha, ubicación y cualquier otro detalle...'
                        )}
                        data-testid="input-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full bg-gold-500 hover:bg-gold-600 text-white font-lato uppercase tracking-wider py-6"
                      data-testid="submit-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t('Sending...', 'Enviando...')}
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          {t('Send Message', 'Enviar Mensaje')}
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Booking Types */}
      <section className="py-20 bg-cream" data-testid="booking-types">
        <div className="container mx-auto px-4 md:px-8">
          <h2 className="font-playfair text-3xl font-bold text-charcoal text-center mb-12">
            {t('Available For', 'Disponible Para')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: t('Church Services', 'Servicios de Iglesia'), desc: t('Sabbath worship, special services', 'Adoración de Sábado, servicios especiales') },
              { title: t('Evangelistic Campaigns', 'Campañas Evangelísticas'), desc: t('Revival meetings, crusades', 'Reuniones de reavivamiento, cruzadas') },
              { title: t('Concerts', 'Conciertos'), desc: t('Solo concerts, collaborative events', 'Conciertos solistas, eventos colaborativos') },
              { title: t('Special Events', 'Eventos Especiales'), desc: t('Weddings, memorials, dedications', 'Bodas, memoriales, dedicaciones') },
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <h3 className="font-playfair text-lg font-semibold text-charcoal mb-2">{item.title}</h3>
                <p className="font-lato text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
